import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { StoreCategoryScope } from "./storeCategoryMode";

export type StorePresentation = {
  release_id: string; period: {start:string;end:string};
  gender_inventory: {label:string;stock_units:number}[];
  weekday_days: {label:string;previous_days:number}[];
};
type Result={key:string;data?:StorePresentation;error?:string};
const Context=createContext<{data?:StorePresentation;loading:boolean;error?:string}>({loading:false});
export const useStoreAnalysisPresentation=()=>useContext(Context);

// Optional, release-checked GET for screen additions only. Existing report and
// workbook requests remain byte-for-byte unchanged.
export function StoreAnalysisPresentationProvider({scope,children}:{scope:StoreCategoryScope;children:ReactNode}) {
  const key=JSON.stringify([scope.releaseId,scope.params]);
  const [result,setResult]=useState<Result>({key:""});
  const cache=useRef(new Map<string,StorePresentation>());
  useEffect(()=>{
    const cached=cache.current.get(key);
    if(cached){setResult({key,data:cached});return;}
    const controller=new AbortController();let active=true;
    setResult({key});
    void(async()=>{
      try{
        const response=await fetch(`/api/v1/store/presentation?${scope.params}`,{signal:controller.signal});
        if(!response.ok)throw Error("Mağaza ekranının ek alanları açılamadı.");
        const data:StorePresentation=await response.json(),params=new URLSearchParams(scope.params);
        if(data.release_id!==scope.releaseId||data.period?.start!==params.get("start")||data.period?.end!==params.get("end"))throw Error("Veri sürümü değişti. Sayfayı yenileyin.");
        if(!Array.isArray(data.gender_inventory)||data.gender_inventory.some(row=>typeof row.label!=="string"||!Number.isFinite(row.stock_units))||!Array.isArray(data.weekday_days)||data.weekday_days.some(row=>typeof row.label!=="string"||!Number.isSafeInteger(row.previous_days)||row.previous_days<0))throw Error("Ek ekran alanlarının doğrulaması tamamlanamadı.");
        if(!active)return;
        if(cache.current.size>=8)cache.current.delete(cache.current.keys().next().value!);
        cache.current.set(key,data);setResult({key,data});
      }catch(error){if(active)setResult({key,error:error instanceof Error?error.message:"Ek ekran alanları açılamadı."});}
    })();
    return()=>{active=false;controller.abort();};
  },[key]);
  const current=result.key===key?result:undefined;
  return <Context.Provider value={{data:current?.data,error:current?.error,loading:!current?.data&&!current?.error}}>{children}</Context.Provider>;
}
